import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import { toBoolean } from '@publicss/utils';
import classNames from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import { gsap } from 'gsap';
import React, { useState, useEffect } from 'react';
import ReactCompareImage from 'react-compare-image';
import styled from 'styled-components';

import {
  PageBodyBlock_SpecsFields,
  PageBodyBlock_SpecsPrimary,
} from '../../../types/prismic';
import imageBlank from '../../assets/images/image--blank-01.jpg';
import imageMexican from '../../assets/images/image--taco-01.jpg';
import { usePageContext } from '../../providers/PageContext';
import { media } from '../../styles/media';
import { useIsMobile } from '../../utils/breakpoints';
import { Button } from '../ui';

const Section = styled.section`
  .legend-item {
    max-width: 156px;
    width: 100%;
    p {
      margin-bottom: 0px;
      min-width: 69px;
    }
  }
  .cato,
  .specs-slider--height,
  .specs-slider--height [data-testid='container'] {
    height: 400px !important;
    //padding-bottom: 63%;
    overflow: hidden;
    @media (min-width: 1024px) {
      height: 520px !important;
    }
    @media ${media.xlUp} {
      height: 583px !important;
    }
  }
  #specs-slider {
    opacity: 0;
    //transition: all 0.3s linear;
    &.is-active {
      opacity: 1;
    }
  }
  .spec-slider-bg {
    position: absolute;
    height: 100%;
    width: 100%;
    background: #45423b;
    img {
      opacity: 0.45;
    }
  }
  .nav--specs {
    border-bottom: 1px solid var(--grey-3);
    ul {
      display: flex;
    }
    a {
      position: relative;
      color: var(--grey-5);
      overflow: hidden;
      &::before {
        position: absolute;
        content: '';
        bottom: 0;
        width: 100%;
        inset-inline-start: -100%;
        height: 2px;
        background: var(--primary);
        transition: all 0.15s linear;
      }
      &.is-active {
        color: var(--grey-6);
        &::before {
          inset-inline-start: 0;
        }
      }
    }
  }
  .btn--specs {
    background: var(--white);
    background-image: url("data:image/svg+xml,%3Csvg width='22' height='14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.07 1.624L14.658 3.04l4.242 4.242-4.242 4.243 1.414 1.414 4.243-4.243 1.414-1.414-5.657-5.657z' fill='%23fff'/%3E%3Cmask id='a' maskUnits='userSpaceOnUse' x='14' y='1' width='8' height='12'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.07 1.624L14.658 3.04l4.242 4.242-4.242 4.243 1.414 1.414 4.243-4.243 1.414-1.414-5.657-5.657z' fill='%23fff'/%3E%3C/mask%3E%3Cg mask='url(%23a)'%3E%3Cpath fill='%2344433F' d='M1-6.203h30v30H1z'/%3E%3C/g%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5.93 1.624L7.342 3.04 3.101 7.28l4.242 4.243-1.414 1.414-4.243-4.243L.272 7.281 5.93 1.624z' fill='%23fff'/%3E%3Cmask id='b' maskUnits='userSpaceOnUse' x='0' y='1' width='8' height='12'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5.93 1.624L7.342 3.04 3.101 7.28l4.242 4.243-1.414 1.414-4.243-4.243L.272 7.281 5.93 1.624z' fill='%23fff'/%3E%3C/mask%3E%3Cg mask='url(%23b)'%3E%3Cpath fill='%2344433F' d='M21-6.203H-9v30h30z'/%3E%3C/g%3E%3C/svg%3E");
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 50%;
    height: 66px;
    width: 66px;
  }
`;

interface BlockSpecsSliceProps {
  slice: {
    primary: PageBodyBlock_SpecsPrimary;
    items: PageBodyBlock_SpecsFields[];
  };
}

const BlockSpecsSlice: React.FunctionComponent<BlockSpecsSliceProps> = ({
  slice,
}) => {
  const { siteTag } = usePageContext();
  const isCloudKitchens = siteTag === 'site:cloudkitchens';
  const { primary, items } = slice;
  const isMobile = useIsMobile();

  const [activeComponent] = useState(items[0]?.category?.text);

  function accordion(e) {
    const current = e.currentTarget;
    const { id } = current.dataset;
    const accordionContainer = document.querySelector(
      `.accordion[data-id="${id}"]`
    );
    const accordionTrigger = document.querySelector(
      `.accordion--trigger[data-id="${id}"]`
    );
    if (accordionContainer) accordionContainer.classList.toggle('is-active');
    if (accordionTrigger) accordionTrigger.classList.toggle('is-active');
  }

  function switchSpec(e) {
    const current = e.currentTarget;
    const { id } = current.dataset;
    const image = document.querySelector('[data-testid="right-image"]');
    const buttons = document.querySelectorAll('.nav--specs a');
    buttons.forEach(function (value) {
      if (value.dataset.id === id) {
        value.classList.add('is-active');
      } else {
        value.classList.remove('is-active');
      }
    });
    const item = items.find((obj) => obj.category.text === id);
    if (image) {
      image.src = item.category_image.url;
    }
  }

  useEffect(() => {
    const fadeUp = document.querySelectorAll('#block-specs .fade-up');

    fadeUp.forEach(function (element) {
      gsap.from(element, {
        scrollTrigger: {
          trigger: element,
          start: 'top 90%',
        },
        duration: 0.5,
        opacity: '0',
        y: '20%',
      });
    });
  }, []);

  const {
    title,
    eyebrow,
    description,
    button,
    title_two,
    description_two,
    legend_list_item_selected,
    legend_list_item_unselected,
    kitchen_spec_one,
    kitchen_spec_two,
    kitchen_spec_three,
    kitchen_spec_four,
  } = primary;

  const blockSpecs = useMergePrismicPreviewData(
    useStaticQuery(graphql`
      {
        allPrismicKitchenSpec {
          edges {
            node {
              _previewable
              prismicId
              data {
                column_title {
                  html
                  richText
                  text
                }
                options {
                  included_for_free
                  option_name {
                    richText
                    html
                    text
                  }
                }
              }
            }
          }
        }
      }
    `)
  );

  const allIDS = [
    kitchen_spec_one?.id,
    kitchen_spec_two?.id,
    kitchen_spec_three?.id,
    kitchen_spec_four?.id,
  ].filter((el) => el !== undefined);

  const filteredBlockSpecs = allIDS
    .map((id) => {
      return blockSpecs.allPrismicKitchenSpec.edges.find(
        (edge) => edge.node.prismicId === id
      );
    })
    .filter(toBoolean);

  return (
    <Section id="block-specs" className="bg--grey-2">
      <div className="container">
        <div className="fade-up mb-20 hidden grid-cols-4 md:mb-24 md:grid md:grid-cols-8 lg:mb-40 lg:grid-cols-12">
          <div className="col-span-8 lg:col-span-10 lg:col-start-2">
            <div className="specs-slider--height">
              <ReactCompareImage
                leftImage={imageBlank}
                rightImage={imageMexican}
                sliderLineWidth={0}
                handle={
                  <button
                    type="button"
                    className="btn--specs"
                    aria-label="slide to reveal images"
                  />
                }
              />
            </div>
            <div className="nav--specs flex items-center justify-between">
              <ul>
                {items.map((item, index) => {
                  const { category } = item;
                  return (
                    <li key={index} className="inline-block">
                      <a
                        onClick={switchSpec}
                        data-id={category.text}
                        className={`eyebrow me-6 block pb-8 pt-12 lg:me-12 ${
                          activeComponent === category.text ? 'is-active' : ''
                        }`}>
                        {asText(category.richText)}
                      </a>
                    </li>
                  );
                })}
              </ul>
              <p className="tiny color--grey-4 equipment mb-0 text-right">
                * Equipment shown is for illustrative purposes only and may vary
                per facility and by actual equipment you bring.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 md:grid-cols-9 lg:grid-cols-12">
          <div className="col-span-2">
            <div className="top-160">
              <div className="eyebrow p5 line color--grey-4 fade-up mb-10 lg:mb-0">
                {asText(eyebrow.richText)}
              </div>
            </div>
          </div>

          <div className="col-span-4 md:col-span-9 lg:col-span-10">
            <div className="grid grid-cols-4 md:grid-cols-9 lg:grid-cols-10">
              <div className="col-span-4 mb-10 md:col-span-5 lg:col-span-7">
                <h2 className="h2 color--primary fade-up mb-2 md:mb-0">
                  {asText(title.richText)}
                </h2>
              </div>
              <div className="col-span-4 col-start-1">
                <p className="fade-up mb-12">{asText(description.richText)}</p>
                <Button globalTypeform fullWidth={isMobile}>
                  {asText(button.richText)}
                </Button>
              </div>
              <div className="fade-up hidden md:col-span-4 md:col-start-6 md:block lg:col-span-3 lg:col-start-8">
                <div className="flex">
                  <ul className="ul--specs me-10">
                    <li className="legend-item">
                      <PrismicRichText
                        field={legend_list_item_selected.richText}
                      />
                    </li>
                  </ul>
                  <ul className="ul--specs">
                    <li className="open legend-item">
                      <PrismicRichText
                        field={legend_list_item_unselected.richText}
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 md:hidden">
              <div className="mt-12" />

              {filteredBlockSpecs?.map(
                (
                  {
                    node: {
                      data: { column_title, options },
                    },
                  },
                  i
                ) => {
                  return (
                    <div key={i} className="col-span-4">
                      <div
                        className="accordion--trigger flex justify-between"
                        onClick={accordion}
                        data-id={`${i}`}>
                        <p className="h5 mb-0">
                          {asText(column_title.richText)}
                        </p>
                        <div className="ms-auto">
                          <svg
                            width="26"
                            height="26"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <rect
                              x=".5"
                              y=".5"
                              width="25"
                              height="25"
                              rx="12.5"
                              stroke="#B5B3AB"
                            />
                            <path
                              d="M16.664 12.168h-3.168V9h-1.344v3.168H9v1.344h3.152v3.152h1.344v-3.152h3.168v-1.344z"
                              fill="#B5B3AB"
                            />
                          </svg>
                        </div>
                      </div>{' '}
                      <div className="accordion" data-id={`${i}`}>
                        <div className="pb-4 pe-10 pt-6">
                          <ul className="ul--specs">
                            {options.map(
                              ({ included_for_free, option_name }, index) => (
                                <li
                                  key={index}
                                  className={included_for_free ? '' : 'open'}>
                                  {asText(option_name.richText)}
                                </li>
                              )
                            )}
                            {i === 1 && (
                              <div className="fade-up col-span-4 mt-5 md:col-span-12">
                                {isCloudKitchens && (
                                  <p className="small color--grey-6">
                                    *Level of service dependent on facility
                                  </p>
                                )}
                              </div>
                            )}
                          </ul>
                        </div>
                      </div>
                      <hr className="mb-10 mt-10" />
                    </div>
                  );
                }
              )}
            </div>

            <div className="mt-20 hidden grid-cols-4 md:grid md:grid-cols-12 lg:mt-24">
              {filteredBlockSpecs?.map(
                (
                  {
                    node: {
                      data: { column_title, options },
                    },
                  },
                  idx
                ) => {
                  return (
                    <div
                      key={idx}
                      className="fade-up pe-4 md:col-span-6 md:mb-20 lg:col-span-3 lg:mb-0">
                      <h3 className="h5 mb-6">
                        {asText(column_title.richText)}
                      </h3>
                      <ul className="ul--specs">
                        {options.map(
                          ({ included_for_free, option_name }, idx) => (
                            <li
                              key={idx}
                              className={classNames({
                                open: !included_for_free,
                              })}>
                              {asText(option_name.richText)}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  );
                }
              )}
              <div className="fade-up col-span-4 mt-5 md:col-span-12">
                {isCloudKitchens && (
                  <p className="small color--grey-6">
                    *Level of service dependent on facility
                  </p>
                )}
              </div>
              <div className="fade-up col-span-3 mt-20">
                <h3 className="h5">{asText(title_two.richText)}</h3>
              </div>
              <div className="fade-up col-span-6 mt-20">
                <p className="small color--grey-5">
                  {asText(description_two.richText)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default BlockSpecsSlice;
